/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   7/29/20 3:54 PM
 */

import React, { useState } from "react";
import {
	Box,
	Divider,
	Fade,
	Grid,
	IconButton,
	Link,
	ListItemText,
	MenuItem,
	MenuList,
	Popover,
	Tooltip,
	Typography
} from "@mui/material";
import Avatar from "../components/Avatar";
import { useSelector } from "react-redux";
import { State } from "../state/store";
import { Link as RouterLink } from "react-router-dom";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import Const from "../const.json";
import EmployeeSettingsDialog from "../calendar/EmployeeSettingsDialog";
import { useQuery } from "urql";
import { EmployeeQuery, EmployeeQueryResponse, EmployeeQueryVars } from "../graphql/queries/employees";

export default function UserMenu() {

	const user = useSelector((s: State) => s.user);

	const [ employeeResult ] = useQuery<EmployeeQueryResponse, EmployeeQueryVars>({
		query: EmployeeQuery,
		variables: {
			id: String(user?.id)
		}
	})

	const [ settingsDialogOpen, setSettingsDialogOpen ] = useState(false);
	const [ anchorEl, setAnchorEl ] = useState<HTMLButtonElement | null>(null);
	const isOpen = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const menuIconSx = { mr: 1, width: "24px" };

	return <>
		<Tooltip title={"User Menu"} placement={"right"}>
			<IconButton onClick={handleClick} size="large">
				<Avatar id={user?.id as string} size={"lg"}/>
			</IconButton>
		</Tooltip>
		<Popover
			open={isOpen}
			anchorEl={anchorEl}
			onClose={handleClose}
			TransitionComponent={Fade}
			anchorOrigin={{
				vertical: "center",
				horizontal: "right",
			}}
			transformOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
		>
			<Box sx={{ ml: 1, mr: 1, maxWidth: "300px" }}>
				<Grid container wrap={"nowrap"} alignItems={"center"} justifyContent={"space-between"}>
					<Grid item sx={{ p: 2 }}>
						<Avatar id={user?.id as string}/>
					</Grid>
					<Grid item sx={{ pt: 2, pr: 2, pb: 2 }}>
						<div>
							<Typography variant={"subtitle1"}>{user?.fullName || user?.id}</Typography>
						</div>
						<div>
							<Typography variant={"body2"} color={"textSecondary"}>
								{user?.businessUnit}
							</Typography>
						</div>
					</Grid>
				</Grid>
				<Divider variant={"fullWidth"}/>
				<MenuList>
					<MenuItem component={Link} href={Const.URL_CONTACT} target="_blank" color={"textPrimary"}>
						<HelpOutlineIcon sx={menuIconSx}/>
						<ListItemText>
							Contact us
						</ListItemText>
					</MenuItem>
					<Divider/>
					<MenuItem onClick={() => {setSettingsDialogOpen(true); handleClose(); }}>
						<SettingsIcon sx={menuIconSx}/>
						<ListItemText>
							Profile Settings
						</ListItemText>
					</MenuItem>
					<Divider/>
					<MenuItem component={RouterLink} to={"/logout"}>
						<ExitToAppIcon sx={menuIconSx}/>
						<ListItemText>
							Sign out
						</ListItemText>
					</MenuItem>
				</MenuList>
			</Box>
		</Popover>
		{employeeResult.data?.employee &&
			<EmployeeSettingsDialog
				employee={employeeResult.data.employee}
				open={settingsDialogOpen}
				onClose={() => {
					setSettingsDialogOpen(false);
				}}
			/>}
	</>;

}